import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface HoldToConfirmButtonProps {
  onConfirm: () => void;
  holdDurationMs?: number;
  disabled?: boolean;
  loading?: boolean;
  idleLabel: string;
  holdingLabel?: string;
  icon?: ReactNode;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
}

export function HoldToConfirmButton({
  onConfirm,
  holdDurationMs = 1500,
  disabled = false,
  loading = false,
  idleLabel,
  holdingLabel = "Hold to confirm…",
  icon,
  className,
  size = "lg",
}: HoldToConfirmButtonProps) {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const holdStartRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const completedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    holdStartRef.current = null;
    setIsHolding(false);
    setProgress(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startHold = useCallback(() => {
    if (disabled || loading || isHolding) return;
    completedRef.current = false;
    holdStartRef.current = performance.now();
    setIsHolding(true);
    setProgress(0);

    const tick = (now: number) => {
      if (holdStartRef.current === null) return;
      const elapsed = now - holdStartRef.current;
      const p = Math.min(1, elapsed / holdDurationMs);
      setProgress(p);
      if (p >= 1) {
        completedRef.current = true;
        cleanup();
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
          try { navigator.vibrate(50); } catch { /* noop */ }
        }
        onConfirm();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [disabled, loading, isHolding, holdDurationMs, onConfirm, cleanup]);

  const cancelHold = useCallback(() => {
    if (completedRef.current) return;
    const start = holdStartRef.current;
    const wasHolding = isHolding;
    const elapsed = start !== null ? performance.now() - start : 0;
    cleanup();
    if (wasHolding && elapsed < 200) {
      toast({
        title: "Press and hold to confirm",
        description: "Hold the button for a moment to trigger this action.",
      });
    }
  }, [cleanup, isHolding]);

  return (
    <Button
      type="button"
      size={size}
      disabled={disabled || loading}
      onPointerDown={(e) => {
        e.preventDefault();
        startHold();
      }}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      onBlur={cancelHold}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if ((e.key === " " || e.key === "Enter") && !e.repeat) {
          e.preventDefault();
          startHold();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          cancelHold();
        }
      }}
      aria-label={`${idleLabel} (press and hold to confirm)`}
      className={cn(
        "relative overflow-hidden select-none touch-none",
        className,
      )}
    >
      {/* Progress fill overlay */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 bg-white/25 pointer-events-none"
        style={{ width: `${progress * 100}%`, transition: "none" }}
      />
      <span className="relative z-10 flex items-center justify-center">
        {loading ? (
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        ) : (
          icon
        )}
        {isHolding ? holdingLabel : idleLabel}
      </span>
    </Button>
  );
}
